import * as React from "react";
import {DateRangePreset} from "./analytics.constants";
import {formatFriendlyDate, todayStr} from "./analytics.format";
import {getCountryName} from "../../../../utils/countryUtil";

interface IDateRangeFilterProps {
    preset: DateRangePreset;
    appliedRange: { startDate: string; endDate: string };
    customStart: string;
    customEnd: string;
    customError: string | null;
    selectedCountry: string;
    countryOptions: string[];
    setPreset: (p: DateRangePreset) => void;
    setCustomStart: (v: string) => void;
    setCustomEnd: (v: string) => void;
    setSelectedCountry: (v: string) => void;
    onPresetClick: (value: Exclude<DateRangePreset, "custom">) => void;
    onCustomApply: () => void;
}

export const DateRangeFilter: React.FC<IDateRangeFilterProps> = ({
    preset, appliedRange, customStart, customEnd, customError, selectedCountry, countryOptions,
    setPreset, setCustomStart, setCustomEnd, setSelectedCountry, onPresetClick, onCustomApply,
}) => {
    return (
        <div className="admin-analytics__sticky-filters" data-testid="date-range-filter">
            <div className="admin-analytics__date-filters">
                <div className="admin-analytics__filter-row">
                    <div className="admin-analytics__preset-group" role="group" aria-label="Quick date range">
                        <span className="admin-analytics__date-filter-label">Period:</span>
                        <button className={`admin-analytics__date-btn${preset === "30d" ? " selected" : ""}`}
                                onClick={() => onPresetClick("30d")}>Last 30 Days
                        </button>
                        <button className={`admin-analytics__date-btn${preset === "3m" ? " selected" : ""}`}
                                onClick={() => onPresetClick("3m")}>Last 3 Months
                        </button>
                        <button className={`admin-analytics__date-btn${preset === "12m" ? " selected" : ""}`}
                                onClick={() => onPresetClick("12m")}>Last 12 Months
                        </button>
                        <button className={`admin-analytics__date-btn${preset === "custom" ? " selected" : ""}`}
                                onClick={() => setPreset("custom")}>Custom Range
                        </button>
                    </div>
                    <div className="admin-analytics__country-filter" role="group" aria-label="Country filter">
                        <label htmlFor="analytics-country-filter"
                               className="admin-analytics__date-filter-label">Country:</label>
                        <select
                            id="analytics-country-filter"
                            className="admin-analytics__country-select"
                            value={selectedCountry}
                            onChange={(e) => setSelectedCountry(e.target.value)}
                        >
                            <option value="">All Countries</option>
                            {countryOptions.map((code) => (
                                <option key={code} value={code}>{getCountryName(code)}</option>
                            ))}
                        </select>
                        {selectedCountry && (
                            <button className="admin-analytics__country-clear"
                                    onClick={() => setSelectedCountry("")}
                                    aria-label="Clear country filter">Clear</button>
                        )}
                    </div>
                    <div className="admin-analytics__range-display">
                        {formatFriendlyDate(appliedRange.startDate)}
                        {" "}&mdash;{" "}
                        {formatFriendlyDate(appliedRange.endDate)}
                    </div>
                </div>
                {preset === "custom" && (
                    <div className="admin-analytics__custom-range" role="group" aria-label="Custom date range">
                        <div className="admin-analytics__custom-inputs">
                            <label className="admin-analytics__custom-label"
                                   htmlFor="analytics-start-date">From</label>
                            <input
                                id="analytics-start-date"
                                type="date"
                                className="admin-analytics__date-input"
                                value={customStart}
                                max={customEnd || todayStr()}
                                onChange={(e) => setCustomStart(e.target.value)}
                            />
                            <label className="admin-analytics__custom-label" htmlFor="analytics-end-date">To</label>
                            <input
                                id="analytics-end-date"
                                type="date"
                                className="admin-analytics__date-input"
                                value={customEnd}
                                min={customStart}
                                max={todayStr()}
                                onChange={(e) => setCustomEnd(e.target.value)}
                            />
                            <button className="admin-analytics__date-btn selected"
                                    onClick={onCustomApply}>Apply
                            </button>
                        </div>
                        {customError &&
                            <div className="admin-analytics__custom-error" role="alert">{customError}</div>}
                    </div>
                )}
            </div>
        </div>
    );
};
